import createClient, { type Middleware } from 'openapi-fetch';
import type { paths } from './schema';

/**
 * How requests carry the session, which lives in cookies the browser sends
 * by itself (same origin): the CSRF token every change echoes, and a way to
 * renew the session after a 401.
 */
export interface Session {
  /** The token to send as X-CSRF-Token on a change; null when signed out. */
  csrf: () => string | null;
  /**
   * Renews the session after a request sent with the CSRF token `seen` came
   * back 401. True if the request may be sent again.
   */
  refresh: (seen: string | null) => Promise<boolean>;
}

/** A problem response from the API (RFC 9457), with its status. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * How the web app names itself to the API, on every request it makes: the
 * API counts active readers by the app they used.
 */
export const CLIENT = { 'X-Wellread-Client': 'web' } as const;

const SAFE = new Set(['GET', 'HEAD', 'OPTIONS']);

/** Puts the session's CSRF token on a change; GETs need none. */
export function withCsrf(request: Request, csrf: string | null): Request {
  if (!SAFE.has(request.method) && csrf) request.headers.set('X-CSRF-Token', csrf);
  return request;
}

export function createApi(baseUrl: string, session: Session) {
  const client = createClient<paths>({ baseUrl, credentials: 'same-origin', headers: CLIENT });
  // The CSRF token each request went with, and a copy to send again: a fetched request's body is spent.
  const sent = new WeakMap<Request, { csrf: string | null; copy: Request }>();
  const auth: Middleware = {
    onRequest({ request }) {
      const csrf = session.csrf();
      withCsrf(request, csrf);
      sent.set(request, { csrf, copy: request.clone() });
      return request;
    },
    // A 401 may only mean the access cookie expired on the way: renew the
    // session and ask once more. A 401 that comes back from that is the
    // session's end.
    async onResponse({ request, response, options }) {
      if (response.status !== 401) return undefined;
      const s = sent.get(request);
      if (!s || !(await session.refresh(s.csrf))) return undefined;
      // Called on its own: the browser's fetch refuses to run as another object's method.
      const send = options.fetch;
      return send(withCsrf(s.copy, session.csrf()));
    },
  };
  client.use(auth);

  /** Unwraps an openapi-fetch result, throwing ApiError on failure. */
  async function call<T>(p: Promise<{ data?: T; error?: unknown; response: Response }>): Promise<T> {
    const { data, error, response } = await p;
    if (!response.ok) {
      const problem = (error ?? {}) as { detail?: string; title?: string };
      throw new ApiError(response.status, problem.detail ?? problem.title ?? response.statusText, error);
    }
    return data as T;
  }

  return { client, call, baseUrl, session };
}

export type Api = ReturnType<typeof createApi>;
